/* eslint-disable react/jsx-no-bind */
// eslint-disable-next-line import-helpers/order-imports
import { useContext, useEffect, useRef, useState } from 'react';

import { Box, Drawer, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { wellzTheme } from '@wellz-theme/mui';
import { infoSessionCancelledAtom } from 'atoms/inviteSession';
import { useAtomValue } from 'jotai';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { isEmpty } from 'utils/helpers/lodash';
import '@fullcalendar/react/dist/vdom';

import FullCalendar, { EventApi, EventClickArg } from '@fullcalendar/react';

// eslint-disable-next-line import-helpers/order-imports
import listPlugin from '@fullcalendar/list';
// eslint-disable-next-line import-helpers/order-imports
import timeGridPlugin from '@fullcalendar/timegrid';

import { AgendaContext, NotificationContext, useModals } from 'context';
import { useCareMatrix } from 'hooks/useCareMatrix';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import useRequest from 'hooks/useRequest';
import {
  AllowDayAndTime,
  SpecialistSession,
  SpecialistSessionResponse,
  deleteBooking,
  getSpecialistAvailability,
  getSpecialistSessions,
} from 'services';

import { CareMatrixModule } from 'components/CareMatrixModule';

import { getUnavailableHours } from './utils';

import { Loading } from '..';

import { Direction, Event, WeekRange } from './types';

import * as S from './styles';

const AgendaCalendar: React.FC = () => {
  const calendarRef = useRef<FullCalendar>(null);
  const intl = useIntl();
  const isMobile = window.innerWidth < SCREEN_MIN_WIDTH;
  const { openModule, sessionInfo, isModuleOpen } = useCareMatrix();
  const infoSessionCancelled = useAtomValue(infoSessionCancelledAtom);

  const { agenda, setAgenda } = useContext(AgendaContext);
  const { setNotification } = useContext(NotificationContext);

  const [initialRender, setInitialRender] = useState<boolean>(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [currentWeekRange, setCurrentWeekRange] = useState<WeekRange>(
    {} as WeekRange,
  );
  const [initialWeek] = useState<string>(() => {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day;

    return new Date(date.setDate(diff)).toISOString();
  });
  const [availabilityDays] = useState<Record<string, number>>({
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  });

  const [currentSession, setCurrentSession] = useState<Event>();

  const { closeModal } = useModals();

  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getAvailabilityRequest = useRequest();

  useEffect(() => {
    if (!sessionInfo) setCurrentSession(undefined);
  }, [sessionInfo]);

  const mapOccasionalAvailability = (
    occasionalAvailbility?: AllowDayAndTime[],
  ) => {
    if (occasionalAvailbility) {
      return occasionalAvailbility
        .map(({ allow_period: allowPeriod, block_period: blockPeriod }) => {
          if (allowPeriod) {
            return {
              display: 'background',
              start: allowPeriod.start,
              end: allowPeriod.end,
              classNames: ['allow-period'],
            };
          }
          if (blockPeriod) {
            return {
              display: 'background',
              start: blockPeriod.start,
              end: blockPeriod.end,
              id: 'blockPeriod',
              classNames: ['block-period'],
            };
          }
          return undefined;
        })
        .filter(Boolean);
    }

    return [];
  };

  const mapRecurrentAvailabilities = (
    occasionalAvailbility?: AllowDayAndTime[],
  ) => {
    if (occasionalAvailbility) {
      return occasionalAvailbility
        .map(({ allow_day_and_time: allowDayAndTime }) => {
          if (allowDayAndTime) {
            return {
              display: 'background',
              startTime: allowDayAndTime.start,
              endTime: allowDayAndTime.end,
              daysOfWeek: [availabilityDays[allowDayAndTime.day]],
              classNames: ['allow-day-and-time'],
            };
          }
          return undefined;
        })
        .filter(Boolean);
    }

    return [];
  };

  async function deleteBoking(bookingId: string) {
    try {
      await deleteBooking(bookingId);

      const calendar = calendarRef.current?.getApi();

      if (calendar) {
        const calendarEvents = calendar.getEvents();

        const calendarEvent = calendarEvents.find((e: EventApi) => {
          return e.extendedProps.bookingId === bookingId;
        });

        calendarEvent?.remove();

        closeModal('calendarEvent');

        setNotification({
          message: intl.formatMessage({
            id: 'CALENDAR_EVENT.TOAST.SUCCESS',
          }),
          type: 'success',
        });
      }
    } catch {
      setNotification({
        message: intl.formatMessage({
          id: 'CALENDAR_EVENT.TOAST.FAIL',
        }),
        type: 'failure',
      });
    }
  }

  const updateCurrentUserModuleIfIsOpen = (sessions: Event[]) => {
    if (currentSession) {
      const session = sessions.find(
        sess => sess.bookingId === currentSession.bookingId,
      );

      if (session) {
        openModule(session.endUserCognitoId, {
          deleteBooking: deleteBoking,
          session: session as Event,
        });
      }
    }
  };

  const {
    isLoading: isLoadingSessions,
    refetch: reftechGetSpecialistSessions,
  } = useQuery<SpecialistSessionResponse>({
    queryKey: [
      'getSpecialistSessions',
      currentTimeZone,
      currentWeekRange.startAt,
      currentWeekRange.endAt,
    ],
    cacheTime: 0,
    queryFn: () =>
      getSpecialistSessions(
        currentTimeZone,
        currentWeekRange.startAt,
        currentWeekRange.endAt,
      ),
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
    onSuccess: (response: SpecialistSessionResponse) => {
      const sessions: Event[] = response.data.map(
        (session: SpecialistSession) => ({
          id: session.id,
          bookingId: session.booking_id,
          endUserCognitoId: session.end_user_cognito_id,
          title: session.end_user_full_name,
          status: session.status,
          end: session.end_at,
          start: session.start_at,
          classNames: ['fc-custom-event'],
          canJoin: session.can_join,
          canJoinAfterDate: session.can_join_after_date,
          rescheduledSession: session.rescheduled_session,
        }),
      );

      const occasionalAvailbility = mapOccasionalAvailability(
        agenda?.availability_constraints,
      );

      const recurrentUnavailabilities = getUnavailableHours(
        mapRecurrentAvailabilities(agenda?.availability_constraints),
      );

      setEvents([
        ...sessions,
        ...occasionalAvailbility,
        ...recurrentUnavailabilities,
      ]);

      updateCurrentUserModuleIfIsOpen(sessions);
    },
  });

  const getAvailability = async () => {
    try {
      getAvailabilityRequest.setLoading();
      // eslint-disable-next-line camelcase
      const { availability_constraints, timezone } =
        await getSpecialistAvailability();

      setAgenda({ availability_constraints, timezone });

      getAvailabilityRequest.setSuccess();
    } catch (e) {
      getAvailabilityRequest.setError(e, undefined, true);
    }
  };

  function handleWeekChange(direction: Direction) {
    const calendar = calendarRef.current?.getApi();

    if (calendar) {
      let end;
      let start;

      if (direction === 'previous') {
        end = calendar.view.activeStart.toISOString();
        start = new Date(
          calendar.view.activeStart.setDate(
            calendar.view.activeStart.getDate() - 7,
          ),
        ).toISOString();
      } else if (direction === 'next') {
        start = calendar.view.activeEnd.toISOString();
        end = new Date(
          calendar.view.activeEnd.setDate(
            calendar.view.activeEnd.getDate() + 7,
          ),
        ).toISOString();
      } else {
        calendar.view.calendar.today();
        start = calendar.view.currentStart.toISOString();
        end = calendar.view.currentEnd.toISOString();
      }

      setCurrentWeekRange({
        startAt: start,
        endAt: end,
      });
    }
  }

  function handleEventClick(info: EventClickArg) {
    const { start, end, extendedProps, classNames, id } = info.event;

    if (start && end && !classNames.includes('fc-bg-event')) {
      const session: Event = {
        id,
        start: start.toISOString(),
        end: end.toISOString(),
        bookingId: extendedProps.bookingId,
        endUserCognitoId: extendedProps.endUserCognitoId,
        canJoin: extendedProps.canJoin,
        canJoinAfterDate: extendedProps.canJoinAfterDate,
        rescheduledSession: extendedProps.rescheduledSession,
      } as Event;

      if (isMobile) {
        openModule(extendedProps.endUserCognitoId, {
          session,
          deleteBooking: deleteBoking,
        });
        return;
      }

      setCurrentSession(session);

      openModule(extendedProps.endUserCognitoId, {
        deleteBooking: deleteBoking,
        session,
      });
    }
  }

  useEffect(() => {
    if (isEmpty(agenda)) {
      getAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda]);

  useEffect(() => {
    if (calendarRef.current && initialRender) {
      document
        .querySelector(
          `[title="Previous ${intl.formatMessage({
            id: 'MY_AGENDA.CALENDAR.BUTTONS.WEEK',
          })}"]`,
        )
        ?.addEventListener('click', () => handleWeekChange('previous'));

      document
        .querySelector(
          `[title="Next ${intl.formatMessage({
            id: 'MY_AGENDA.CALENDAR.BUTTONS.WEEK',
          })}"]`,
        )
        ?.addEventListener('click', () => handleWeekChange('next'));

      document
        .querySelector(
          `[title="This ${intl.formatMessage({
            id: 'MY_AGENDA.CALENDAR.BUTTONS.WEEK',
          })}"]`,
        )
        ?.addEventListener('click', () => handleWeekChange('current'));

      const calendar = calendarRef.current.getApi();

      const weekRange = {
        startAt: calendar.view.activeStart.toISOString(),
        endAt: calendar.view.activeEnd.toISOString(),
      };

      setCurrentWeekRange(weekRange);
      setInitialRender(false);
    }

    return () => {
      document.removeEventListener('click', () => handleWeekChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarRef.current]);

  useEffect(() => {
    reftechGetSpecialistSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoSessionCancelled]);

  // AgendaTags does not have access to yoga and intl
  const tagsConfigs = [
    {
      key: intl.formatMessage({
        id: 'CPAGENDA.TEXT.AVAILABLE',
      }),
      color: '#fff',
    },
    {
      key: intl.formatMessage({
        id: 'CPAGENDA.TEXT.EXCEPTIONALLYAVAILABLE',
      }),
      color: '#C1EEDB',
    },
    {
      key: intl.formatMessage({
        id: 'CPAGENDA.TEXT.BUSY',
      }),
      color: '#C8C8C8',
    },
    {
      key: intl.formatMessage({
        id: 'CPAGENDA.TEXT.EXCEPTIONALLYBUSY',
      }),
      color: '#FCD6C3',
    },
  ];

  const AgendaTags = (
    <S.TagsContainer className="tags">
      {tagsConfigs.map(tag => (
        <S.Tag key={tag.key}>
          <S.TagColor hasBorder={tag.color === '#fff'} color={tag.color} />
          {tag.key}
        </S.Tag>
      ))}
    </S.TagsContainer>
  );

  // In order to be able to inject a component inside of the calendar we need to do it via DOM manipulation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const afterToolbarRef = useRef(null) as any;

  useEffect(() => {
    const toolbarElement = document.querySelector('.fc-header-toolbar');

    if (toolbarElement && tagsConfigs) {
      const placeholder = document.createElement('div');

      toolbarElement.parentNode?.insertBefore(
        placeholder,
        toolbarElement.nextSibling,
      );

      ReactDOM.render(AgendaTags, placeholder);

      afterToolbarRef.current = placeholder;
    }

    return () => {
      if (afterToolbarRef.current) {
        ReactDOM.unmountComponentAtNode(afterToolbarRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsConfigs]);

  return (
    <S.CalendarWrapper>
      <Loading
        isLoading={getAvailabilityRequest.isLoading() || isLoadingSessions}
      />
      <FullCalendar
        ref={calendarRef}
        height="auto"
        plugins={[timeGridPlugin, listPlugin]}
        initialView={isMobile ? 'listWeek' : 'timeGridWeek'}
        headerToolbar={{
          left: isMobile ? 'prev,title,next' : 'prev,next today',
          center: isMobile ? '' : 'title',
          right: isMobile ? 'today' : 'timeGridWeek,listWeek',
        }}
        buttonIcons={{
          prev: 'fc-icon-left-single-arrow',
          next: 'fc-icon-right-single-arrow',
        }}
        buttonText={{
          today: intl.formatMessage({
            id: 'MY_AGENDA.CALENDAR.BUTTONS.TODAY',
          }),
          week: intl.formatMessage({ id: 'MY_AGENDA.CALENDAR.BUTTONS.WEEK' }),
          list: intl.formatMessage({ id: 'MY_AGENDA.CALENDAR.BUTTONS.LIST' }),
        }}
        locale={navigator.language}
        nowIndicator
        expandRows
        eventOverlap={false}
        editable={false}
        allDaySlot={false}
        events={events}
        eventClick={handleEventClick}
        validRange={{
          start: initialWeek,
        }}
        listDaySideFormat={false}
        listDayFormat={props => {
          return (
            <Typography
              variant="body2"
              color={wellzTheme.palette.primary.main}
              display="flex"
              alignItems="center"
            >
              <Typography
                variant="h6"
                component="span"
                color={wellzTheme.palette.primary.main}
              >
                {props.date.day}
              </Typography>
              {` ${new Intl.DateTimeFormat(navigator.language, {
                month: 'short',
                weekday: 'long',
                timeZone: 'UTC',
              }).format(props.date.marker)}`}
            </Typography>
          ) as unknown as string; // this is necessary to be able to return an element instead of a string
        }}
        eventTimeFormat={props =>
          `${new Intl.DateTimeFormat(navigator.language, {
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'UTC',
          }).format(props.date.marker)}`
        }
        displayEventEnd={!isMobile}
      />
      {isMobile && isModuleOpen && (
        <Drawer
          open={isModuleOpen && isMobile}
          sx={{ '.MuiDrawer-paper': { backgroundColor: 'white' } }}
          PaperProps={{ sx: { width: '100%' } }}
          anchor="right"
        >
          <Box width="100%" height="100%">
            <CareMatrixModule />
          </Box>
        </Drawer>
      )}
    </S.CalendarWrapper>
  );
};

export default AgendaCalendar;
